import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './MediaBlock.module.scss';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { Field, Placeholder, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import ScRichText from 'components/Generic/SitecoreWrappers/RichText';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import useComponentIndex from 'hooks/useComponentIndex';

type Item = {
  title?: Field<string>;
  cta?: { value: { text: string } };
  description?: Field<string>;
};

type Fields = {
  title: Field<string>;
  items?: Array<{ fields: Item }>;
  description?: Field<string>;
};

type Rendering = {
  params?: {
    presentationType?: 'textLink' | 'buttonPrimary' | 'buttonSecondary';
  };
};

type MediaBlockProps = ComponentProps & {
  fields: Fields;
  rendering: Rendering;
};

const BUTTON_TYPES = {
  textLink: 'textLink',
  buttonPrimary: 'primary',
  buttonSecondary: 'secondary',
};

const MediaBlock: React.FC<MediaBlockProps> = ({ fields, rendering }) => {
  const [isTopPaddingShort, setIsTopPaddingShort] = useState(false);
  const [isBottomPaddingShort, setIsBottomPaddingShort] = useState(false);
  const { ref, componentIndex } = useComponentIndex();
  const { sitecoreContext } = useSitecoreContext();

  const textBlock = fields.items?.[0];
  const renderButton = sitecoreContext.pageEditing || textBlock?.fields?.cta?.value?.text;
  const buttonType =
    BUTTON_TYPES[rendering?.params?.presentationType ?? 'buttonPrimary'] ?? 'primary';

  useEffect(() => {
    if (!ref.current) return;

    const lightBackgrounds = [
      'rgba(0, 0, 0, 0)',
      'rgb(255, 255, 255)',
      'rgba(255, 255, 255, 0)',
      'white',
    ];
    const parent = ref.current.parentElement;

    if (parent) {
      const children = Array.from(parent.children);

      if (componentIndex > 0) {
        const topElement = children[componentIndex - 1];
        const topBackground = window.getComputedStyle(topElement).backgroundColor;
        setIsTopPaddingShort(lightBackgrounds.includes(topBackground));
      }

      if (componentIndex < children.length - 1) {
        const bottomElement = children[componentIndex + 1];
        const bottomBackground = window.getComputedStyle(bottomElement).backgroundColor;
        setIsBottomPaddingShort(lightBackgrounds.includes(bottomBackground));
      }
    }
  }, [componentIndex, ref]);

  return (
    <section
      ref={ref}
      data-e2e-component-uid={rendering?.uid}
      className={classNames(
        'default-section-padding background-to-change link-change',
        styles.mediaBlock,
        {
          'short-padding-top': isTopPaddingShort,
          'short-padding-bottom': isBottomPaddingShort,
        }
      )}
    >
      <div className="container">
        <Text tag="h2" className="background-to-change mb-5" field={fields.title} />
        {fields.items && fields.items.length > 0 && (
          <div className="row">
            <div className="col-12 col-md-6">
              <Text
                tag="h3"
                className="mb-2 background-to-change"
                field={textBlock?.fields.title}
                data-testid="text-title"
              />
              {textBlock && (
                <ScRichText
                  className="content-description body-default quote-description text-grey background-to-change"
                  fields={textBlock.fields}
                  rendering={rendering}
                  data-testid="text-description"
                />
              )}
              <Placeholder
                rendering={rendering}
                name="abrdn-list"
                className={`${styles.placeholder} mt-2 mb-3`}
                data-testid="list"
              />
              {renderButton && (
                <AbrdnButton
                  buttonType={buttonType}
                  className="mt-2 mt-md-3 background-to-change"
                  linkField={textBlock?.fields.cta}
                  data-testid="cta"
                />
              )}
            </div>
            <div className="col-12 col-md-6 mt-5 mt-md-0">
              <Placeholder
                rendering={rendering}
                name="abrdn-media-item"
                className={styles.placeholder}
                articleBody={true}
                data-testid="media-item"
              />
              <Text
                tag="p"
                className="background-to-change caption mt-3"
                field={fields.description}
                data-testid="description"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MediaBlock;
